import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btn, btnGold, btnRed, field, when } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";

const METHODS = [["check", "Check"], ["ach", "ACH"], ["cash", "Cash"], ["wire", "Wire"]];
const today = () => new Date().toLocaleDateString("en-CA");

export function ReceivePaymentForm({ inv, onDone, tid = "fin-inv-receive" }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ received_on: today(), method: "check", reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`${tid}-open`} className={btnGold}>
      Mark paid · {money(inv.balance_due)} received
    </button>
  );
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post(`/invoices/${inv.id}/receive`, v); toast.success("Payment recorded · invoice paid"); setOpen(false); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`${tid}-form`} className="p-3 border border-nex-gold/30 rounded-sm space-y-2 bg-white/[0.02]">
      <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Employer payment · full amount {money(inv.balance_due)} · resident balances are not changed</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <input type="date" required max={today()} value={v.received_on} onChange={set("received_on")} data-testid={`${tid}-date`} className={field} />
        <select value={v.method} onChange={set("method")} data-testid={`${tid}-method`} className={field}>
          {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={v.reference} onChange={set("reference")} placeholder="Check / reference #" data-testid={`${tid}-reference`} className={field} />
        <input value={v.note} onChange={set("note")} placeholder="Note (optional)" data-testid={`${tid}-note`} className={field} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} data-testid={`${tid}-cancel`} className={btn}>Cancel</button>
        <button type="submit" disabled={busy} data-testid={`${tid}-submit`} className={btnGold}>{busy ? "Saving…" : "Record payment"}</button>
      </div>
    </form>
  );
}

function PaymentRow({ p, i, inv, canReverse, onDone }) {
  const [mode, setMode] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const reverse = async () => {
    setBusy(true);
    try { await finApi.post(`/invoices/${inv.id}/payments/${i}/reverse`, { reason }); toast.success("Payment reversed"); setMode(false); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`fin-inv-payment-${i}`} className="space-y-1">
      <div className="flex flex-wrap items-center gap-3 text-xs text-nex-silver">
        <span className={`font-mono ${p.reversed ? "line-through text-nex-muted" : "text-nex-gold"}`}>{money(p.amount)}</span>
        <span className="text-nex-muted">{p.paid_by?.name} · {p.received_on || when(p.paid_at)}</span>
        {p.method && <span className="text-nex-muted uppercase font-mono text-[10px]">{p.method}{p.reference ? ` #${p.reference}` : ""}</span>}
        {p.memo && <span className="text-nex-muted">· {p.memo}</span>}
        {p.reversed && <span data-testid={`fin-inv-payment-${i}-reversed`} className="text-orange-300">Reversed · {p.reverse_reason} ({p.reversed_by?.name}, {when(p.reversed_at)})</span>}
        {canReverse && !p.reversed && !mode && <button type="button" onClick={() => setMode(true)} data-testid={`fin-inv-payment-${i}-reverse`} className={`${btnRed} ml-auto`}>Reverse</button>}
      </div>
      {mode && (
        <div className="flex flex-wrap items-center gap-2">
          <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this payment being reversed?" data-testid={`fin-inv-payment-${i}-reason`} className={`${field} flex-1 min-w-[240px]`} />
          <button type="button" onClick={() => setMode(false)} className={btn} data-testid={`fin-inv-payment-${i}-cancel`}>Cancel</button>
          <button type="button" disabled={busy || !reason.trim()} onClick={reverse} data-testid={`fin-inv-payment-${i}-confirm`} className={btnRed}>Confirm reversal</button>
        </div>
      )}
    </div>
  );
}

export function PaymentsList({ inv, onDone }) {
  const isSuper = useIsSuper();
  if (!inv.payments?.length) return null;
  return (
    <div data-testid="fin-inv-payments" className="space-y-2">
      {inv.payments.map((p, i) => <PaymentRow key={i} p={p} i={i} inv={inv} canReverse={isSuper && !p.txn_ids?.length} onDone={onDone} />)}
    </div>
  );
}
